// Bridges a guest's trivia quiz answers across the signup/login redirect.
//
// Browser-only. Import this from the CLIENT Inertia entrypoint only — never
// from the SSR entrypoint, since it touches `window`/`localStorage`.
//
// A guest can play up to 10 questions before creating an account. Their
// answers are tracked in React state only (no DB writes while anonymous).
// Once they choose to sign up or log in, that state is about to be lost to a
// hard page redirect, so it's stashed here first and claimed into a real
// QuizSession/QuizAnswer set once `current_user` is present again.

const STORAGE_KEY = "amdb.guest-quiz-answers.v1"

export type PendingAnswer = { question_id: number; answer_id: number }

export function savePendingAnswers(answers: PendingAnswer[]): void {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(answers))
  } catch {
    // Private browsing / storage quota — nothing else to do, the guest just
    // won't have their score bridged across the redirect.
  }
}

function loadPendingAnswers(): PendingAnswer[] | null {
  if (typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : null
  } catch {
    return null
  }
}

function clearPendingAnswers(): void {
  if (typeof window === "undefined") return
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

let attempted = false

/**
 * Claims any pending guest quiz answers once the visitor is authenticated.
 * Safe to call on every page load — no-ops if there's nothing pending, no
 * user, or a claim has already been attempted this app boot. Browser-only.
 */
export async function claimPendingQuizAnswers(currentUser: unknown): Promise<void> {
  if (attempted || typeof window === "undefined" || !currentUser) return
  const answers = loadPendingAnswers()
  if (!answers || answers.length === 0) return
  attempted = true

  try {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? ""
    const res = await fetch("/quiz/claim", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf },
      body: JSON.stringify({ answers }),
    })
    if (!res.ok) return

    const data = await res.json()
    clearPendingAnswers()
    window.dispatchEvent(new CustomEvent("guest-quiz:claimed", { detail: data }))
  } catch {
    // Leave the pending answers in place — they'll be retried on next boot.
  }
}
